

import { Button } from '@/shared/ui/button';
import {
    DialogFooter
} from '@/shared/ui/dialog';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/shared/ui/form';
import { Input } from '@/shared/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { mastersApi } from '@/features/masters/api';
import { Profile, UserRole } from '@/features/users/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

// バリデーションスキーマ
const formSchema = z.object({
    username: z.string().trim().min(1, "ユーザー名は必須です。").max(50, "ユーザー名は50文字以内です。"),
    name: z.string().trim().min(1, "氏名は必須です。").max(50, "氏名は50文字以内です。"),
    role: z.nativeEnum(UserRole, { message: "権限を選択してください。" }),
    is_active: z.boolean(),
    craftsman_id: z.string().nullable(),
    // email: z.string().email("有効なメールアドレスを入力してください。").optional(), // 将来的に追加するかも
});

type UserFormData = z.infer<typeof formSchema>;

/**
 * ユーザー編集フォームのプロパティ
 */
interface EditUserFormProps {
    user: Profile;
    onClose: () => void;
    onSave: (userId: string, data: Pick<UserFormData, 'role' | 'is_active' | 'craftsman_id'>) => Promise<void>;
}

// 簡単な職人型（getCraftsmen関数の戻り値に合わせる）
interface SimpleCraftsman {
    id: string;
    name: string;
}

/**
 * ユーザー編集フォーム
 * @param props プロパティ
 * @returns ユーザー編集フォーム
 */
export default function EditUserForm({ user, onClose, onSave }: EditUserFormProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [craftsmen, setCraftsmen] = useState<SimpleCraftsman[]>([]);
    const [craftsmenLoading, setCraftsmenLoading] = useState(false);

    /**
     * フォーム
     */
    const form = useForm<UserFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: user.username || user.email?.split('@')[0] || '',
            name: user.name || '',
            role: user.role || UserRole.USER,
            is_active: user.is_active ?? true,
            craftsman_id: user.craftsman_id || null
        },
    });

    // ユーザーデータが変更された時にフォームをリセット
    useEffect(() => {
        const formValues = {
            username: user.username || user.email?.split('@')[0] || '',
            name: user.name || '',
            role: user.role || UserRole.USER,
            is_active: user.is_active ?? true,
            craftsman_id: user.craftsman_id || null
        };
        form.reset(formValues);
    }, [user, form]);

    // 職人データを取得
    useEffect(() => {
        const fetchCraftsmen = async () => {
            setCraftsmenLoading(true);
            try {
                const data = await mastersApi.craftsmen.getAll();
                // dataがundefinedまたはnullの場合は空配列を使用
                const safeCraftsmenData = Array.isArray(data) ? data : [];
                setCraftsmen(safeCraftsmenData.map((craftsman: SimpleCraftsman) => ({
                    id: craftsman.id,
                    name: craftsman.name
                })));
            } catch (error) {
                console.error('職人データの取得に失敗しました:', error);
                toast.error('職人データの読み込みに失敗しました');
                // エラー時は空配列を設定
                setCraftsmen([]);
            } finally {
                setCraftsmenLoading(false);
            }
        };

        fetchCraftsmen();
    }, []);


    const onSubmit = async (values: UserFormData) => {
        setIsLoading(true);
        try {
            const updateData = {
                username: values.username,
                name: values.name,
                role: values.role,
                is_active: values.is_active,
                craftsman_id: values.craftsman_id
            };
            await onSave(user.id, updateData);
            toast.success("ユーザー情報が更新されました。");
            onClose();
        } catch (error) {
            console.error("Failed to update user in EditUserForm:", error);
            const errorMessage = error instanceof Error ? error.message : "ユーザー情報の更新に失敗しました。";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>ユーザー名</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="ユーザー名を入力"
                                    autoComplete="off"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                ログイン時に使用するユーザー名です。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>氏名</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="氏名を入力"
                                    autoComplete="off"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                表示用の氏名です。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>権限</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="権限を選択" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {Object.values(UserRole).map((role) => (
                                        <SelectItem key={role} value={role}>
                                            {role === UserRole.ADMIN ? 'ADMIN' : 'USER'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="craftsman_id"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>職人との紐づけ</FormLabel>
                            <Select
                                onValueChange={(value) => {
                                    // "none"の場合はnullを設定、それ以外はそのまま設定
                                    field.onChange(value === "none" ? null : value);
                                }}
                                value={field.value ? field.value : "none"}
                                disabled={craftsmenLoading}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="職人を選択（紐づけない場合は空白）" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="none">紐づけなし</SelectItem>
                                    {craftsmen.map((craftsman) => (
                                        <SelectItem key={craftsman.id} value={craftsman.id}>
                                            {craftsman.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormDescription>
                                このユーザーに紐づける職人を選択します。紐づけるとカレンダーで担当予定が強調表示されます。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                            <div className="space-y-0.5">
                                <FormLabel className="text-base">有効状態</FormLabel>
                                <FormDescription>
                                    ユーザーアカウントを有効または無効にします。
                                </FormDescription>
                            </div>
                            <FormControl>
                                <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                            </FormControl>
                        </FormItem>
                    )}
                />
                <DialogFooter className="pt-4">
                    <Button type="button" variant="outline" onClick={onClose} className="mr-2">キャンセル</Button>
                    <Button type="submit" disabled={isLoading || !form.formState.isDirty}>
                        {isLoading ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> 保存中...</>
                        ) : (
                            '変更を保存'
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
} 